import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DatePicker } from '@tcc/crate';
import { Table } from '@tcc/fds-table';

import { useAppDispatch } from 'app/hooks';
import { useReviewScriptsTable } from './useReviewScriptsTable';
import { useBulkGeneratePlantingPrescription } from '../../common/hooks';
import { useGetActivitiesForFields } from '../hooks/useGetActivitiesForFields/useGetActivitiesForFields';
import { getMessage } from 'util/locale';
import { navigateToUrl } from 'util/routes';
import { useRedirectUrl } from 'util/hooks/useRedirectUrl';
import { activityTypes } from 'constants/activities';
import { addErrorToast, addSuccessToast } from 'messages';
import { getAgronomicSeasonCodeForCountryCode } from 'advanced/selectors';

import BulkLoading, { JobStatus } from 'common/BulkLoading';
import ScriptDetails from './ScriptDetails';
import type { FormData } from '../../common/types';
import { SaveStatus, type PrescriptionSavingStatus } from '../hooks/useSavePrescriptions/types';
import { SEASONS_NAMES_BY_CODE } from 'constants/advanced';

interface ReviewScriptsProps {
  allFailed: boolean;
  allSaved: boolean;
  areErrors: boolean;
  isSaving: boolean;
  prescriptionSaveStatuses: PrescriptionSavingStatus[];
  resetPrescriptionStatuses: () => void;
  setIsGeneratingPlantingPrescriptions: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReviewScripts = memo(function ReviewScripts({
  allFailed,
  allSaved,
  areErrors,
  isSaving,
  prescriptionSaveStatuses,
  resetPrescriptionStatuses,
  setIsGeneratingPlantingPrescriptions,
}: ReviewScriptsProps) {
  const { getValues, control } = useFormContext<FormData>();
  const [
    plannedPlantingDate,
    fields,
    grainPrice,
    season
  ] = getValues(['plannedPlantingDate', 'fields', 'grainPrice', 'season']);
  const { fields: formFields, remove } = useFieldArray<FormData, 'fields', '_id'>({
    control,
    name: 'fields',
    // useFieldArray overwrites the id (field id) from the data. To avoid that, we set a custom keyName
    keyName: '_id',
  });
  const redirectUrl = useRedirectUrl();
  const hasInitializedRef = useRef(false);
  const dispatch = useAppDispatch();

  const onErrorSaving = useCallback((statuses: PrescriptionSavingStatus[]) => {
    // TODO: Change this text for something more specific depending on the number of prescriptions 
    // that failed, at least indicating if some or all failed.
    dispatch(addErrorToast(getMessage('help-text__failure--save-prescriptions')));
    
    const fieldIndexesToRemove = statuses
    .filter(({ status }) => status === SaveStatus.SUCCEEDED)
    .map(({ fieldId }) => formFields.findIndex((formField) => formField.id === fieldId))
    .filter((index) => index !== -1);

    remove(fieldIndexesToRemove);
  }, [dispatch, formFields, remove]);

  const onAllSaved = useCallback(() => {
    dispatch(addSuccessToast(getMessage('text__success--save')));
    setTimeout(() => navigateToUrl(redirectUrl), 1000);
  }, [dispatch, redirectUrl]);

  const saveStatuses = useMemo(() => prescriptionSaveStatuses.map((status) => ({
    ...status,
    label: fields.find((field) => field.id === status?.fieldId)?.name,
    id: status?.fieldId,
  })), [prescriptionSaveStatuses, fields]);

  const onErrorGeneratingPrescriptions = useCallback((fieldId: string) => {
    dispatch(
      addErrorToast(getMessage(
        'help-text__failure--generate-zones',
        { fieldName: fields.find((field) => field.id === fieldId)?.name || '' })));
  }, [dispatch, fields]);

  const onAllComplete = useCallback(() => {
    setIsGeneratingPlantingPrescriptions(false);
  }, [setIsGeneratingPlantingPrescriptions]);

  const {
    initBulkGeneration,
    fieldStatuses: prescriptionGenerationStatuses,
    isLoading: isGeneratingPlantingPrescriptions
  } = useBulkGeneratePlantingPrescription({
    onFieldError: onErrorGeneratingPrescriptions,
    onAllComplete,
    setIsGeneratingPlantingPrescriptions
  });

  useGetActivitiesForFields({
    activityType: activityTypes.PLANTING,
    fieldUuids: fields.map(({ id }) => id),
  });

  useEffect(() => {
    if (hasInitializedRef.current || !fields?.length) return;

    const fieldsWithRecommendation = fields.filter((field) => field.recommendation);
    if (fieldsWithRecommendation.length === 0) return;

    const countryCode = fieldsWithRecommendation[0].countryCode;
    const defaultSeasonCodeByCountry = getAgronomicSeasonCodeForCountryCode(countryCode);
    const seasonForRecommendation = season || SEASONS_NAMES_BY_CODE[defaultSeasonCodeByCountry];

    hasInitializedRef.current = true;
    initBulkGeneration({
      countryCode,
      fields: fieldsWithRecommendation,
      zoneGenerationInfo: {
        models: ['YIELD', 'CCI'],
      },
      season: seasonForRecommendation,
    });
  }, [fields, initBulkGeneration, season, setIsGeneratingPlantingPrescriptions]);

  useEffect(() => {
    if (allSaved) {
      onAllSaved();
    }
  
    if (areErrors && !isSaving) {
      onErrorSaving(prescriptionSaveStatuses);
      resetPrescriptionStatuses();
    }
  }, [allSaved, areErrors, isSaving, onErrorSaving, onAllSaved, prescriptionSaveStatuses, resetPrescriptionStatuses]);

  const fieldsWithGeneratedPrescription = useMemo(() => fields.filter((field) => prescriptionGenerationStatuses.find(
    ({ fieldId, status }) => fieldId === field.id && status === JobStatus.SUCCEEDED
  )), [fields, prescriptionGenerationStatuses]);

  const table = useReviewScriptsTable({ fields: fieldsWithGeneratedPrescription, grainPrice, control });

  if (isGeneratingPlantingPrescriptions) {
    return (
      <BulkLoading
        items={prescriptionGenerationStatuses}
        isLoading={isGeneratingPlantingPrescriptions}
      />
    );
  }

  if (isSaving || allSaved || allFailed) {
    return (
      <BulkLoading
        items={saveStatuses}
        isLoading={isSaving}
      />
    );
  }

  return (
    <>
      <div className="review-scripts-header">
        <div>
          <p>{getMessage('bulk-fvss-review-scripts-header-body')}</p>
          <span>{getMessage('bulk-fvss-review-scripts-header-description')}</span>
        </div>
        <DatePicker
          label={getMessage('title-text__planting-date')}
          disabled
          moment={moment}
          selected={plannedPlantingDate}
          testId="planned-planting-date-picker"
        />
      </div>
      <Table
        table={table}
        isReady
        emptyTableContent={{ message: '' }}
        showPagination={false}
        expandedRowComponent={ScriptDetails}
      />
    </>
  );
});
